import * as React from "react";
import { useEffect, useState } from "react";
import Centre from "../../../domain/Centre";
import { UUID } from "../../../domain/UUID";
import { Card, CardContent, CardHeader, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import Season, { SeasonMetadata } from "../../../domain/Season";
import SeasonState from "../../../domain/SeasonState";
import { getAllSeasons, getSeason } from "../../../gateway/exams";
import { getStudents } from "../../../gateway/students";
import { useCurrentSeason } from "../../exams/SeasonContext";
import IconCardTitle from "../../common/IconCardTitle";
import AdvancedStudentsCard from "./AdvancedStudentsCard";
import BfiStudentsCard from "./BfiStudentsCard";

interface Props {
  readonly centreId: UUID<Centre>
}

export default function PreviousSeasonsSection({ centreId }: Props) {
  const { currentSeason } = useCurrentSeason();
  const [previousSeasons, setPreviousSeasons] = useState<SeasonMetadata[] | undefined>(undefined);
  const [selectedSeasonId, setSelectedSeasonId] = useState<UUID<Season> | undefined>(undefined);
  const [selectedSeason, setSelectedSeason] = useState<Season | undefined>(undefined);

  useEffect(() => {
    Promise.all([getAllSeasons(), getStudents({ centreId })])
      .then(([seasons, students]) => {
        const seasonIdsWithStudents = new Set(students.map(s => s.seasonId));
        const previous = seasons
          .filter(s => s.id !== currentSeason?.id)
          .filter(s => SeasonState.isOrIsAfter(s.state, SeasonState.ROUND_1_RESULTS_PUBLICATION))
          .filter(s => seasonIdsWithStudents.has(s.id))
          .sort((a, b) => b.year - a.year);
        setPreviousSeasons(previous);
        setSelectedSeasonId(previous[0]?.id);
      });
  }, [centreId, currentSeason?.id]);

  useEffect(() => {
    setSelectedSeason(undefined);
    if (selectedSeasonId !== undefined) {
      getSeason(selectedSeasonId).then(setSelectedSeason);
    }
  }, [selectedSeasonId]);

  if (previousSeasons === undefined || previousSeasons.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={<IconCardTitle icon={<HistoryIcon fontSize="large" />} text="Previous years" />}
        action={
          <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
            <InputLabel id="previous-season-label">Year</InputLabel>
            <Select
              labelId="previous-season-label"
              label="Year"
              value={selectedSeasonId ?? ''}
              onChange={e => setSelectedSeasonId(e.target.value as UUID<Season>)}
            >
              {previousSeasons.map(s => <MenuItem key={s.id} value={s.id}>{s.year}</MenuItem>)}
            </Select>
          </FormControl>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Results and certificates for students registered at this centre in previous years.
          </Typography>
          {selectedSeason === undefined
            ? <Typography>Loading</Typography>
            : <>
                <AdvancedStudentsCard key={`advanced-${selectedSeason.id}`} season={selectedSeason} centreId={centreId} readOnly />
                <BfiStudentsCard key={`bfi-${selectedSeason.id}`} season={selectedSeason} centreId={centreId} readOnly />
              </>
          }
        </Stack>
      </CardContent>
    </Card>
  );
}
